// Hurl wheel entries and compatibility refusals until the browser landing plan is ported.
import { resolveCachedItemForRender } from './find-cached-item';
import { encounterCreatureField, itemsCol, layerOf } from '../shared/array-types';
import { pageClearHurlPick, pageHurlPick, pageHurlRowRefusal, pageInventoryRow } from '../shared/object-handling-browser';
import { HURLS_UNATTENDED_OBJECT, hurlObjectRule } from '../janus-engine/actions/object-handling-layers';
import { refuseUnported } from '../janus-engine/unported.js';

type Row = Record<string, unknown>;
type Host = Window & {
    ftGetActiveCasterId?: () => string;
    ftGetActiveEncounterId?: () => string;
    ftFindFullNestedItem?: (id: string) => unknown;
    FateTide?: { casterCache?: Map<string, Row>; encounterCache?: Map<string, Row>; objectCache?: Map<string, unknown> };
    displayWarning?: (message: string) => void;
};
const host = window as Host;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const upper = (value: unknown): string => String(Array.isArray(value) ? value[0] : value ?? '').trim().toUpperCase();
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;

function activeCaster(): { id: string; row: Row } | null {
    const id = upper(host.ftGetActiveCasterId?.());
    const row = id ? host.FateTide?.casterCache?.get(id) ?? null : null;
    return id && row ? { id, row } : null;
}
/** A FULL ability definition the page holds (the caster's catalog first). */
function definition(caster: Row, id: string): Row | null {
    const full = (value: unknown): Row | null => { const row = record(value); return row && record(row.items) && record(row.item_layer_types) ? row : null; };
    const catalog = record(caster.catalog);
    return full(catalog?.[id] ?? catalog?.[id.toLowerCase()]) ?? full(host.FateTide?.objectCache?.get(id))
        ?? (() => { try { return full(host.ftFindFullNestedItem?.(id)); } catch { return null; } })();
}
/** Every ability this caster holds that hurls an unattended object (its compiled ability buckets, deduped by id). */
export function casterHurlAbilities(caster: Row): Row[] {
    const buckets = record(record(record(caster.computed)?.creature)?.abilities) ?? {};
    const seen = new Set<string>(), out: Row[] = [];
    for (const entries of Object.values(buckets)) {
        for (const entry of Array.isArray(entries) ? entries : []) {
            const row = record(entry);
            const id = upper(row?.item_id ?? record(row?.items)?.item_id ?? row?.ability_id ?? entry);
            if (!UUID.test(id) || seen.has(id)) continue;
            seen.add(id);
            const def = definition(caster, id);
            if (def && hurlObjectRule(def, { value: (r, key) => r ? layerOf(r, key) ?? null : null })) out.push(def);
        }
    }
    return out;
}

export interface HurlVerbAction { label: string; icon?: string; enabled?: boolean; reason?: string | null; run: () => void }
/** The object's wheel group: one entry per hurl ability the active caster holds — enabled when THIS object may be hurled by it. */
export function ftHurlObjectActions(inventoryId: string): HurlVerbAction[] {
    const caster = activeCaster();
    if (!caster) return [];
    const encounterId = upper(host.ftGetActiveEncounterId?.() ?? encounterCreatureField(record(caster.row.encounter_creatures), 'encounter_id'));
    const row = (resolveCachedItemForRender(inventoryId) as Row | null) ?? pageInventoryRow(inventoryId, encounterId);
    return casterHurlAbilities(caster.row).map(ability => {
        const abilityId = upper(itemsCol(ability, 'item_id'));
        const name = String(itemsCol(ability, 'item_name') ?? 'Hurl');
        const refusal = pageHurlRowRefusal(caster.row, ability, row);
        return { label: name, icon: HURLS_UNATTENDED_OBJECT, enabled: refusal === null,
            reason: refusal ?? `Hurl this with ${name} — then choose the target`,
            run: () => { ftHurlObjectStart(caster.id, abilityId, inventoryId); } };
    });
}

/** Refuse before picking or arming while the complete browser landing plan is unavailable. */
export function ftHurlObjectStart(casterId: string, abilityId: string, inventoryId: string): boolean {
    refuseUnported('hurl-object:browser-landing-plan-unported', { casterId, abilityId, inventoryId });
    return false;
}

/** Retire a stale picked hurl and refuse before casting; ordinary dispatches return null. */
export function ftHurlBind(input: { casterId: string; abilityId: string; encounterId: string; targetSelection: string;
    center?: { x: number; y: number; z: number } | null }): null {
    const pick = pageHurlPick(input.casterId, input.abilityId);
    if (!pick) return null;
    const refusal = refuseUnported('hurl-object:browser-landing-plan-unported', { ...input, inventoryId: pick.inventoryId });
    pageClearHurlPick();
    if (typeof cancelTargetDraft === 'function') cancelTargetDraft('hurl-object-refused');
    // null means an ordinary non-hurl dispatch to the caller. A refused binding must stop that dispatch.
    const error = new Error(refusal.reason);
    error.name = 'HurlBindingRefused';
    throw error;
}

Object.assign(window, { ftHurlObjectActions, ftHurlObjectStart, ftHurlBind });
